import type { AuditEvent } from '@sirius/contracts/admin'
import type { OperatorColumnFilter } from '~/pages/cabinets/cabinetStatus'
import { gameShowPath } from '~/pages/games/paths'
import { PLATFORM_SETTINGS_PATH } from '~/pages/platformSettings/paths'
import { MEMBERS_TAB_PATH } from '~/pages/settings/settingsTabs'
import type { AdminRole } from '~/providers/authProvider'
import { ACTIVITY_ACTOR_FIELD } from '~/providers/dataProvider'

/** Who looks at the log, and on which tenant's host. */
export type LinkViewer = { role: AdminRole; hostTenantId: string }

function entityPath(
  { entity, entityId, tenant }: AuditEvent,
  { role, hostTenantId }: LinkViewer,
): string | null {
  switch (entity) {
    // a tenant's records open on its own host only: in the platform log,
    // another tenant's location is not found here
    case 'location':
      return tenant?.id === hostTenantId ? `/locations/${entityId}` : null
    case 'cabinet':
      return tenant?.id === hostTenantId ? `/cabinets/${entityId}` : null
    case 'tenant':
      // the platform pages: tenants are read-only for a technician, but only
      // cabinet events reach it
      return role === 'admin' ? `/platform/tenants/${entityId}` : null
    case 'game':
      // a platform record (the events of a tenant's own configuration
      // too): a staff-only page, on every host
      return role === 'admin' ? gameShowPath(entityId) : null
    case 'user':
      // an account: a platform record, like a game
      return role === 'admin' ? `/platform/users/${entityId}` : null
    case 'platform':
      // the platform's settings: a staff-only page, on every host
      return role === 'admin' ? PLATFORM_SETTINGS_PATH : null
    case 'member':
      // the tenant's members, on its own host
      return tenant?.id === hostTenantId ? MEMBERS_TAB_PATH : null
    case 'session':
    case 'invitation':
      // no page for them: the Members tab lists open invitations only
      return null
  }
}

/**
 * The route the entity cell links to, or null when there is nothing to open
 * (a deleted record, a tenant for tenant users, another tenant's record, an
 * entity without a page).
 */
export function entityLink(event: AuditEvent, viewer: LinkViewer) {
  return event.entityExists ? entityPath(event, viewer) : null
}

/** The audit list's actor filter (staff only): the column id is the field. */
export const ACTOR_FIELD = ACTIVITY_ACTOR_FIELD

export function actorColumnFilter(userId: string): OperatorColumnFilter {
  return { id: ACTOR_FIELD, operator: 'eq', value: userId }
}

/** The filtered actor's user id, if the actor column is filtered. */
export function actorFilterValue(
  filters: readonly OperatorColumnFilter[],
): string | undefined {
  const value = filters.find(({ id }) => id === ACTOR_FIELD)?.value
  return typeof value === 'string' ? value : undefined
}

/**
 * The name of a user actor among the rows, with no extra request. The rows
 * of an actor filter are that actor's events, so the name is there.
 */
export function actorNameIn(events: readonly AuditEvent[], userId: string) {
  for (const { actor } of events) {
    if (actor.kind === 'user' && actor.id === userId) {
      return actor.name
    }
  }
  return undefined
}
